/**
 * Tallivo storefront catalogue — the complete Sugar Town shop.
 * Maps Tallivo products into the existing ShopifyProduct card shape so shop
 * UI keeps working. Variants are identified by Tallivo variantId (never SKU).
 * Prices are whole minor units in the store currency (XCD) → divide by 100.
 */
import type { ShopifyProduct } from "@/lib/shopify";

export const TALLIVO_BASE = "https://tallivo.shop";
export const TALLIVO_STORE_ID = "2fafd474-164d-4572-b472-a81a2f335038";
const API = `${TALLIVO_BASE}/api/public/storefronts/${TALLIVO_STORE_ID}`;

// Brand rule: never display these lines. Wines/spirits ARE shown with a no-shipping note.
const HIDDEN = /(baba lullaby|unscented)/i;
export const ALCOHOL = /(\bwines?\b|brandy|\brum\b|liqueur|spirits?)/i;
export const ALCOHOL_NOTE = "Wines and alcohol cannot be shipped — St. Kitts pickup or delivery only.";

interface TallivoDetail {
  title: string;
  handle: string;
  description: string | null;
  seo?: {title?:string|null;description?:string|null};
  media: { url: string; altText: string | null; position: number }[];
  options: { name: string; values: string[] }[];
  variants: {
    variantId: string;
    title: string | null;
    position: number;
    price: { amountMinor: number; currencyCode: string };
    options: { name: string; value: string }[];
  }[];
  categories: { name: string; handle: string }[];
}

export type TallivoProduct = ShopifyProduct & { categories: string[]; brand?: string; seoTitle?:string; seoDescription?:string; stockKnown?:Record<string,boolean> };

let catalogPromise: Promise<TallivoProduct[]> | null = null;

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Tallivo ${r.status}`);
  return r.json();
}

function mapProduct(d: TallivoDetail, stock: Record<string, boolean>): TallivoProduct {
  const variants = [...d.variants].sort((a, b) => a.position - b.position);
  const price = (minor: number, cc: string) => ({ amount: (minor / 100).toFixed(2), currencyCode: cc });
  const min = variants.reduce((m, v) => (v.price.amountMinor < m.price.amountMinor ? v : m), variants[0]);
  return {
    categories: d.categories.map((c) => c.name),
    seoTitle: d.seo?.title ?? undefined,
    seoDescription: d.seo?.description ?? undefined,
    stockKnown: Object.fromEntries(variants.map(v=>[v.variantId,typeof stock[v.variantId]==="boolean"])),
    brand: inferBrand(d.title, d.categories.map((c) => c.name), d.description ?? ""),
    node: {
      id: d.handle,
      title: d.title,
      description: d.description ?? "",
      handle: d.handle,
      priceRange: { minVariantPrice: min ? price(min.price.amountMinor, min.price.currencyCode) : { amount: "0", currencyCode: "XCD" } },
      images: { edges: [...d.media].sort((a, b) => a.position - b.position).map((m) => ({ node: { url: m.url, altText: m.altText } })) },
      variants: {
        edges: variants.map((v) => ({
          node: {
            id: v.variantId,
            title: v.options.map((o) => o.value).join(" / ") || v.title || "Default",
            price: price(v.price.amountMinor, v.price.currencyCode),
            availableForSale: stock[v.variantId] ?? true,
            selectedOptions: v.options,
          },
        })),
      },
      options: d.options.map((o) => ({ name: o.name, values: o.values })),
    },
  };
}

async function loadOne(handle: string): Promise<TallivoProduct | null> {
  try {
    const [d, a] = await Promise.all([
      getJson<TallivoDetail>(`${API}/products/${encodeURIComponent(handle)}`),
      getJson<{ variants: { variantId: string; inStock: boolean | null }[] }>(
        `${API}/products/${encodeURIComponent(handle)}/availability`,
      ).catch(() => ({ variants: [] })),
    ]);
    const stock: Record<string, boolean> = {};
    a.variants.forEach((v) => {if(typeof v.inStock === "boolean")stock[v.variantId] = v.inStock;});
    return mapProduct(d, stock);
  } catch {
    return null;
  }
}

export function loadTallivoCatalog(): Promise<TallivoProduct[]> {
  if (!catalogPromise) {
    catalogPromise = (async () => {
      const list = await getJson<{ products: { handle: string; title: string }[] }>(`${API}/products`);
      const visible = list.products.filter((p) => p.title && !HIDDEN.test(p.title));
      const all = await Promise.all(visible.map((p) => loadOne(p.handle)));
      return all.filter((p): p is TallivoProduct => !!p);
    })().catch((e) => {
      catalogPromise = null;
      throw e;
    });
  }
  return catalogPromise;
}

export async function loadTallivoProduct(handle: string): Promise<TallivoProduct | null> {
  const cached = catalogPromise ? (await catalogPromise.catch(() => [])).find((p) => p.handle === handle) : undefined;
  return cached ?? loadOne(handle);
}

/**
 * Loosely evaluate a legacy Shopify search string (title:/tag:/product_type:)
 * against Tallivo title + categories. Terms after NOT are exclusions.
 */
export function matchesQuery(p: TallivoProduct, query: string | null | undefined): boolean {
  if (!query) return true;
  const hay = `${p.node.title} ${p.categories.join(" ")}`.toLowerCase();
  const re = /(NOT\s+)?(?:\w+):(?:"([^"]+)"|([^\s()]+))/g;
  let include = false;
  let anyInclude = false;
  let m: RegExpExecArray | null;
  while ((m = re.exec(query))) {
    const term = (m[2] || m[3]).toLowerCase();
    if (m[1]) {
      if (hay.includes(term)) return false;
    } else {
      anyInclude = true;
      if (hay.includes(term)) include = true;
    }
  }
  return anyInclude ? include : true;
}

// Brand inference lives in the pure brandInference module so build scripts
// can use it without pulling in the catalogue's browser-side imports.
import { inferBrand, brandMatches } from "./brandInference";
export { inferBrand, BRAND_DISPLAY } from "./brandInference";

export function inBrand(p: TallivoProduct, brand: string): boolean {
  return !brand || p.brand === brand || brandMatches(brand, `${p.node.title} ${p.categories.join(" ")}`);
}

export const TALLIVO_CATEGORY_MATCH: Record<string, RegExp> = {
  condiments: /\b(condiments?|jams?|jell(?:y|ies)|sauces?|seasonings?|dressings?|chutney|salsa|ketchup|syrup|fudge|sugar cake|tamarind balls|cheese|brandy|wines?|liqueur|spirits?)\b/i,
  hair: /\b(hair|shampoo|conditioner|scalp|chebe|batana)\b/i,
  skincare: /\b(skin\s?care|face|facial|lip|eye cream)\b/i,
  "bath-body": /\b(body|soap|bath|lotion|deodorant|shower|spritz|repell[ae]nt|candle|diffuser|toothpaste)\b/i,
  soaps: /\b(soaps?|body cleanser|shower gel|body wash)\b/i,
  "body-care": /\b(body|lotion|deodorant|spritz|repell[ae]nt|toothpaste)\b/i,
  spa: /\b(candle|diffuser|bath salts?|soaking salts?|cafe caribe)\b/i,
  wellness: /\b(tea|teas|tincture|wellness|sea moss|liniment|linament|pain spray|immuno)\b/i,
  "gift-boxes": /\bgift(s| box| set| bundle)?\b/i,
  books: /\b(books?|journals?|publication)\b/i,
  "men-skincare": /\b(nezer|beard|men'?s?|aftershave|razor bump|clear cut|cool & calm)\b/i,
};

export function inCategory(p: TallivoProduct, cat: string | null): boolean {
  if (!cat) return true;
  const labels = `${p.node.title} ${p.categories.join(" ")}`;
  if (cat === "men-skincare") return p.brand === "nezer" || TALLIVO_CATEGORY_MATCH[cat].test(labels);
  if (cat === "skincare" && (p.brand === "nezer" || /cafe caribe/i.test(labels))) return false;
  if ((cat === "soaps" || cat === "bath-body") && /shampoo/i.test(labels)) return false;
  const re = TALLIVO_CATEGORY_MATCH[cat];
  return re ? re.test(labels) : false;
}
